import axios from "axios";
import * as Sentry from "@sentry/react";
import { getAuthRegistry } from "@/lib/auth/AuthRegistry";
import { notifySessionExpired } from "@/lib/session/sessionExpired";
import { bucketFromUrl, spanName, datesVariantSuffix } from "@/lib/telemetry/bucket";

export const apiClient = axios.create({
  baseURL: "",  // Configured by SsoPage or main.tsx after login based on user's region
  headers: { "Content-Type": "application/json" },
});

// Inject Bearer token from AuthRegistry into all requests
apiClient.interceptors.request.use(req => {
  const { accessToken } = getAuthRegistry().getTokens();
  if (accessToken) {
    req.headers = req.headers || {};
    req.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Log all outgoing requests
  /*
  const method = req.method?.toUpperCase() || "GET";
  const url = req.baseURL ? req.baseURL + req.url : req.url;
  // Log params and data if present
  const params = req.params ? JSON.stringify(req.params) : "";
  const data = req.data ? JSON.stringify(req.data) : "";
  console.log(
    `[API REQUEST] ${method} ${url}` +
    (params ? ` | params: ${params}` : "") +
    (data ? ` | data: ${data}` : "")
  );
  */
  // --- TIMING + SENTRY SPAN INSTRUMENTATION ---
  (req as any).__t0 = performance.now();

  // start a manual span so we always get a child under the active transaction
  const url = new URL(req.url!, req.baseURL || window.location.origin);
  const bucket = bucketFromUrl(url.href);
  const { suffix: datesSuffix, count: datesCount } = datesVariantSuffix(url.href, bucket);
  (req as any).__telemetryBucket = bucket;
  (req as any).__sentrySpan = Sentry.startInactiveSpan?.({
    name: spanName(req.method, bucket) + datesSuffix, // ← add "(dates:N)" when relevant
    op: "http.client",
    attributes: {
      "http.method": (req.method || "GET").toUpperCase(),
      "http.url": url.href,
      "http.target": url.pathname + url.search,
      "http.route": bucket,
      "telemetry.bucket": bucket,
      ...(datesCount !== undefined ? { "telemetry.dates.count": datesCount } : {}),
    },
  });

  return req;
});

apiClient.interceptors.response.use(
  (response) => {
    const t0 = (response.config as any).__t0 as number | undefined;
    const span = (response.config as any).__sentrySpan;
    const url = response.request?.responseURL || response.config.url;

    // Normalize to relative path (pathname + search)
    let lookupUrl = url;
    try {
      const urlObj = new URL(url, response.config.baseURL || window.location.origin);
      lookupUrl = urlObj.pathname + urlObj.search; // e.g., "/api/v1/company/timetable/schedule"
    } catch {
      // If URL parsing fails, use as-is
      lookupUrl = url;
    }

    // Try to enrich with Resource Timing (DNS/connect/TTFB/etc.)
    try {
      if (url) {
        const entries = performance.getEntriesByName(lookupUrl, "resource") as PerformanceResourceTiming[];
        const rt = entries?.[entries.length - 1]; // latest matching entry
        if (rt) {
          // Useful fields (ms)
          const dns = rt.domainLookupEnd - rt.domainLookupStart;
          const connect = rt.connectEnd - rt.connectStart;
          const tls = rt.secureConnectionStart > 0 ? (rt.connectEnd - rt.secureConnectionStart) : 0;
          const ttfb = rt.responseStart - rt.requestStart;
          const download = rt.responseEnd - rt.responseStart;
          const total = rt.responseEnd - rt.startTime;

          // Console for quick local debugging
          // eslint-disable-next-line no-console
          console.log("[XHR TIMING]", {
            url,
            status: response.status,
            dns, connect, tls, ttfb, download, total,
          });

          // Attach to Sentry span if present
          if (span?.setAttribute) {
            span.setAttribute("net.dns", dns);
            span.setAttribute("net.connect", connect);
            span.setAttribute("net.tls", tls);
            span.setAttribute("http.ttfb", ttfb);
            span.setAttribute("http.download", download);
            span.setAttribute("http.duration", total);
          }
        }
      }
    } catch { /* ignore timing read errors */ }

    if (span) {
      span.setAttribute?.("http.status_code", response.status);
      try {
        const bucket = bucketFromUrl(lookupUrl);
        const { suffix, count } = datesVariantSuffix(lookupUrl, bucket);
        // update name to include dates suffix (consistent with request)
        // @ts-ignore — not in public types, but Sentry accepts it on the envelope
        if (suffix) span.updateName?.(spanName(response.config.method, bucket) + suffix);
        span.setAttribute?.("http.route", bucket);
        span.setAttribute?.("telemetry.bucket", bucket);
        if (count !== undefined) span.setAttribute?.("telemetry.dates.count", count);
      } catch { }
      span.end?.();
    }

    // fallback plain duration if RT was unavailable
    if (t0) {
      const dur = performance.now() - t0;
      // eslint-disable-next-line no-console
      console.log("[XHR DURATION]", (response.config.url || ""), `${Math.round(dur)}ms`);
    }

    return response;
  },
  (error) => {
    // Show a global "session expired" once on auth failures
    const status = error?.response?.status;
    const url = String(error?.config?.url || "");
    const isLoginCall = /\/auth\/jwt\/login\b/.test(url);

    // For 401/403 (except login calls), notify session expired and suppress error propagation
    // This prevents unhandled rejection noise in Sentry while the user is redirected to login
    if ((status === 401 || status === 403) && !isLoginCall) {
      notifySessionExpired();
      // End Sentry span before suppressing
      const span = error?.config && (error.config as any).__sentrySpan;
      if (span) {
        span.setStatus?.("error");
        span.setAttribute?.("error", "session_expired");
        span.end?.();
      }
      // Return a never-resolving promise to suppress further error propagation
      // The app is in a terminal state anyway — user will be redirected to login
      return new Promise(() => { });
    }

    const span = error?.config && (error.config as any).__sentrySpan;
    if (span) {
      span.setStatus?.("error");
      span.setAttribute?.("error", String(error?.message || "request failed"));
      try {
        const url = String(error?.config?.url || "");
        const bucket = bucketFromUrl(url);
        const { suffix, count } = datesVariantSuffix(url, bucket);
        // @ts-ignore — see note above
        if (suffix) span.updateName?.(spanName(error?.config?.method, bucket) + suffix);
        span.setAttribute?.("http.route", bucket);
        span.setAttribute?.("telemetry.bucket", bucket);
        if (count !== undefined) span.setAttribute?.("telemetry.dates.count", count);
      } catch { }
      span.end?.();
    }
    throw error;
  }
);
